/**
 * treePdfTypes.ts — Canonical types, error-classes, and DEFAULT_OPTIONS
 * for the export-layout-redesign pipeline. Single source of truth for
 * cross-module types.
 *
 * Refs: docs/superpowers/plans/2026-05-18-export-layout-redesign.md Task 1.5.1
 */

import type { LayoutNode, LayoutLink, FanColorMode } from '../types';

// — Re-exports —
export type { LayoutNode, LayoutLink, FanColorMode };
export type { GenealogyEvent, Person, Relationship } from '../types';

// — Geometric types —
export interface Bounds {
  x: number;
  y: number;
  w: number;
  h: number;
}
export type PaperSize =
  'A4' | 'A3' | 'A2' | 'A1' | 'A0' | 'Letter' | 'Custom' | 'Poster70x100' | 'Poster100x140';
export type StyleVariant = 'color' | 'bw' | 'minimal';
export type CardLevel = 'detailed' | 'standard' | 'compact';
export type ExportProfile = 'screen' | 'home-printer' | 'print-shop' | 'archive-deposit';
// FanColorMode is canoniek in types.ts en wordt boven geïmporteerd + ge-re-export
// (voorheen stond hier een kopie die afdreef, o.a. zonder de waaier-
// differentiators BRON/EOL/IMPLEX).
/**
 * Page orientation — `portrait` (tall) or `landscape` (wide). Re-exported as a
 * named type so consumers (ExportDialog, V1 renderer, atlas) avoid local
 * re-declarations that drift apart from the canonical `ExportOptions` shape.
 */
export type Orientation = 'portrait' | 'landscape';
/**
 * Supported export output formats. Distinct from `PaperSize` because PNG/SVG
 * paths bypass the multi-page atlas. Re-exported for the same reason as
 * `Orientation`.
 */
export type OutputFormat = 'pdf' | 'png' | 'svg';

// — Relationship types —
export interface SpousalPair {
  id: string;
  aId: string;
  bId: string;
  aX: number;
  bX: number;
  marriageOrder?: number;
}
export interface SpousalLink {
  sourceX: number;
  sourceY: number;
  sourceH: number;
  targetX: number;
  targetY: number;
  targetH: number;
  relationType: string;
  divorceDate?: string;
}

export type PageCoordinate =
  | { style: 'cartography'; col: string; row: number }
  | { style: 'genealogy'; gen: number; slot: number };

// — Atlas types (Fase 9 / spec §5.6) —
export type MatchLineEdge = 'left' | 'right' | 'top' | 'bottom';

export interface MatchLine {
  /** Edge of the page this match-line lives on. */
  edge: MatchLineEdge;
  /** Display label on this page (referencing the neighbouring page). */
  label: string;
  /** Display label printed on the neighbouring page (the back-reference). */
  neighborLabel: string;
  /**
   * Optional explicit marker (e.g. "[gesplitst]") used when a node had to be
   * split across the boundary as part of the M3-fallback ladder.
   */
  marker?: string;
  /** Position along the edge in mm (Y for left/right, X for top/bottom). */
  position?: number;
}

export interface AtlasPage {
  index: number; // 0-based page-index in this atlas
  bounds: Bounds; // logical bounds in tree-mm (content area)
  bleedBounds: Bounds; // bounds + atlasOverlap on shared edges only
  /**
   * Outer-edge print-bleed bounds (G4-fix). Adds `printBleedMm` only on the
   * edges of the atlas that DO NOT touch a neighbouring page. Distinct from
   * `bleedBounds` which captures inter-page atlasOverlap.
   */
  printBleedBounds: Bounds;
  /**
   * Safe-margin rectangle (G4-fix). Content INSIDE this rectangle is safe
   * from being clipped by printer-tolerance. Equals `bounds` inset by
   * `safeMarginMm` on every side, plus `bindingGutterMm` on the binding side.
   */
  safeBounds: Bounds;
  matchLines: MatchLine[]; // labels along shared edges
  coordinate: PageCoordinate; // page-coordinate-style label
  /** Grid column-index (0-based, left-to-right). */
  col: number;
  /** Grid row-index (0-based, top-to-bottom). */
  row: number;
  /** True when the inner edge had a node-split that needed labelling. */
  hasSplitNode?: boolean;
  /**
   * Roman-numeral generation-labels along the side of the page (C4-fix).
   * Empty when `showRomanGenerationLabels=false` or no nodes have a known
   * generation on this page.
   */
  generationLabels: GenerationLabel[];
}

export interface GenerationLabel {
  /** Generation number (0 = proband row, 1 = first parents row, …). */
  generation: number;
  /** Roman-numeral rendering: 0→"I", 1→"II", …, 9→"X". */
  roman: string;
  /** Y-position in tree-mm where the label should be rendered. */
  y: number;
}

// — Error classes —
export class InvalidProbandError extends Error {
  readonly name = 'InvalidProbandError' as const;
  constructor(msg: string) {
    super(msg);
    Object.setPrototypeOf(this, InvalidProbandError.prototype);
  }
}

export class TreeTooLargeError extends Error {
  readonly name = 'TreeTooLargeError' as const;
  readonly nodeCount: number;
  readonly maxNodes: number;
  constructor(nodeCount: number, maxNodes: number) {
    super(`Tree heeft ${nodeCount} nodes (max ${maxNodes}). Verklein generationDepth.`);
    this.nodeCount = nodeCount;
    this.maxNodes = maxNodes;
    Object.setPrototypeOf(this, TreeTooLargeError.prototype);
  }
}

export class LayoutInvariantViolation extends Error {
  readonly name = 'LayoutInvariantViolation' as const;
  readonly invariantId: string;
  constructor(invariantId: string, msg: string) {
    super(`${invariantId}: ${msg}`);
    this.invariantId = invariantId;
    Object.setPrototypeOf(this, LayoutInvariantViolation.prototype);
  }
}

// — ExportOptions canonical interface —
export interface ExportOptions {
  viewType: 'pedigree' | 'fan' | 'descendants' | 'patrilineal' | 'matrilineal';
  depth: number;
  descendantDepth: number;
  focusPerson: string;
  selectedFields: string[];
  selectedRelations: string[];
  hideLivingPersons: boolean;
  fanColorMode: FanColorMode;
  fanColorField?: string;
  showPedigreeCollapse: boolean;
  showColorBlindPatterns: boolean;
  showDates: boolean;
  fullDates: boolean;
  showJobs: boolean;
  showLocations: boolean;
  dateFormat: 'nl-long' | 'nl-short' | 'gedcom' | 'iso' | 'roman';
  styleVariant: StyleVariant;
  cardLevel: 'auto' | CardLevel;
  pageSize: PaperSize;
  orientation: Orientation;
  scaleMode: 'tree-to-page' | 'page-to-tree';
  allowNodeSplit: boolean;
  atlasOverlapMm: number;
  printBleedMm: number;
  safeMarginMm: number;
  bindingGutterMm: number;
  bindingSide: 'left' | 'right' | 'top' | 'none';
  wordWrap: boolean;
  showMatchLines: boolean;
  showRomanGenerationLabels: boolean;
  pageCoordinateStyle: 'genealogy' | 'cartography';
  includeFooter: boolean;
  includeLegend: boolean;
  includeIndexPage: boolean;
  exportProfile: ExportProfile;
  outputFormat: OutputFormat;
}

// — Derived types —
export type AtlasOptions = Pick<
  ExportOptions,
  | 'pageSize'
  | 'orientation'
  | 'atlasOverlapMm'
  | 'printBleedMm'
  | 'safeMarginMm'
  | 'bindingGutterMm'
  | 'bindingSide'
  | 'allowNodeSplit'
  | 'scaleMode'
  | 'showMatchLines'
  | 'showRomanGenerationLabels'
  | 'pageCoordinateStyle'
>;

export type ProfileConfig = Pick<
  ExportOptions,
  'atlasOverlapMm' | 'printBleedMm' | 'safeMarginMm' | 'bindingGutterMm'
>;

// — Defaults —
export const DEFAULT_OPTIONS: ExportOptions = {
  viewType: 'pedigree',
  depth: 4,
  descendantDepth: 0,
  focusPerson: '',
  selectedFields: [],
  selectedRelations: [],
  hideLivingPersons: false,
  fanColorMode: 'BRANCH',
  showPedigreeCollapse: false,
  showColorBlindPatterns: false,
  showDates: true,
  fullDates: false,
  showJobs: true,
  showLocations: true,
  dateFormat: 'nl-long',
  styleVariant: 'color',
  cardLevel: 'auto',
  pageSize: 'A4',
  orientation: 'landscape',
  scaleMode: 'tree-to-page',
  allowNodeSplit: false,
  atlasOverlapMm: 10,
  printBleedMm: 0,
  safeMarginMm: 5,
  bindingGutterMm: 0,
  bindingSide: 'none',
  wordWrap: true,
  showMatchLines: true,
  showRomanGenerationLabels: true,
  pageCoordinateStyle: 'genealogy',
  includeFooter: true,
  includeLegend: true,
  includeIndexPage: true,
  exportProfile: 'home-printer',
  outputFormat: 'pdf',
};

// — Index / Header / Legend types (Fase 10 + 11) —

/**
 * One row in the alphabetical person-index. Implex (= same person reached via
 * multiple Sosa-paths) collapses into a single entry with multiple sosa[] and
 * pageNumbers[] entries.
 *
 * Spec: §5.7 (Fase 10, M6+E10-fix)
 */
export interface IndexEntry {
  /** Person identifier — used as map-key during implex de-duplication. */
  personId: string;
  /** Surname (zonder prefix). Sort-key — collation runs on this field only. */
  surname: string;
  /** Surname-prefix ("van der", "de", "von"). Display only — never in sort. */
  surnamePrefix: string;
  /** Given names — second-level sort-key + display. */
  givenNames: string;
  /** Birth year (best-effort: explicit year > parseSortableYear fallback). */
  birthYear?: number;
  /** Death year (best-effort same as birthYear). */
  deathYear?: number;
  /** Sosa-numbers under which the person appears. May be empty for non-Sosa nodes. */
  sosa: number[];
  /** 1-based page numbers where the person is rendered. Sorted ascending. */
  pageNumbers: number[];
  /** True when the same person occurs on ≥2 Sosa positions (E10-fix). */
  isImplex: boolean;
}

/**
 * Per-page footer block. Three-column layout: project / datum / pagina-X.
 * Placed inside the safeMargin zone.
 * Spec: §5.8 (Fase 11, C2-fix).
 */
export interface FooterData {
  projectName: string;
  pageNumber: number;
  totalPages: number;
  generatedDate: string;
  researcher?: string;
}

/**
 * One entry in the legend block. Spec: §5.9 (Fase 11, C3-fix).
 */
export interface LegendItem {
  /** Symbol fragment (unicode or short SVG-text). */
  symbol: string;
  /** Human label. */
  label: string;
  /** Optional explanatory line. */
  description?: string;
  /**
   * Optional CSS-color or palette-key. When set, the legend renderer draws a
   * coloured swatch instead of (or next to) the symbol — used for lineage-colour
   * rows (paternal-teal vs maternal-coral).
   */
  swatch?: string;
  /**
   * Optional dash-array for line-rows (biological vs adoptive vs foster).
   * Mirrors `stroke-dasharray` syntax: '' = solid, '4 2' = dashed, etc.
   */
  dashArray?: string;
  /**
   * Optional B&W pattern identifier used when styleVariant='bw' and the entry
   * represents a colour-coded relation that needs a hatching fallback.
   */
  bwPatternId?: string;
}

// — resolveCardLevelByGen helper —
export function resolveCardLevelByGen(
  cardLevel: ExportOptions['cardLevel'],
  maxGen: number
): Record<number, CardLevel> {
  const result: Record<number, CardLevel> = {};
  if (cardLevel !== 'auto') {
    for (let g = 0; g <= maxGen; g++) result[g] = cardLevel;
    return result;
  }
  for (let g = 0; g <= maxGen; g++) {
    if (g <= 3) result[g] = 'detailed';
    else if (g <= 5) result[g] = 'standard';
    else result[g] = 'compact';
  }
  return result;
}
